import React from "react";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  ListChecks,
  MousePointerClick,
} from "lucide-react";

/**
 * Yardım içeriğinin salt-görüntüleme render'ı.
 *
 * Panelden ve admin önizlemesinden AYNI bileşen kullanılır — böylece admin
 * "Önizle" dediğinde gerçekten kullanıcının göreceği şeyi görür.
 *
 * GÜVENLİK: içerik admin tarafından yazılabilir. Tüm alanlar React text
 * node'u olarak basılır; `dangerouslySetInnerHTML` KULLANILMAZ. Bu yüzden
 * metne HTML/script yazılsa bile düz yazı olarak görünür.
 */

const Section = ({ icon: Icon, title, children, tone = "default" }) => {
  const toneClasses =
    tone === "warning"
      ? "bg-amber-50 border-amber-200"
      : "bg-white border-gray-200";
  return (
    <section className={`rounded-lg border p-4 ${toneClasses}`}>
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2">
        {Icon && (
          <Icon
            className={`h-4 w-4 ${tone === "warning" ? "text-amber-600" : "text-green-600"}`}
            aria-hidden="true"
          />
        )}
        {title}
      </h3>
      {children}
    </section>
  );
};

const HelpContentView = ({ doc, onNavigate }) => {
  if (!doc) return null;

  const {
    summary,
    can_do: canDo = [],
    actions = [],
    steps = [],
    warnings = [],
    related = [],
  } = doc;

  return (
    <div className="space-y-4">
      {summary && (
        <Section icon={HelpCircle} title="Bu sayfa ne işe yarar?">
          <p className="text-sm leading-relaxed text-gray-700">{summary}</p>
        </Section>
      )}

      {canDo.length > 0 && (
        <Section icon={CheckCircle2} title="Burada ne yapabilirsiniz?">
          <ul className="space-y-1.5">
            {canDo.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-gray-700">
                <span className="text-green-600 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {actions.length > 0 && (
        <Section icon={MousePointerClick} title="Butonlar ne yapar?">
          <dl className="space-y-2.5">
            {actions.map((action, i) => (
              <div key={i} className="text-sm">
                <dt className="font-medium text-gray-900">{action.label}</dt>
                <dd className="text-gray-600 leading-relaxed mt-0.5">
                  {action.effect}
                </dd>
              </div>
            ))}
          </dl>
        </Section>
      )}

      {steps.map((step, i) => (
        <Section key={i} icon={ListChecks} title={step.title}>
          <ol className="space-y-1.5">
            {(step.items || []).map((item, j) => (
              <li key={j} className="flex gap-2.5 text-sm text-gray-700">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-semibold flex items-center justify-center">
                  {j + 1}
                </span>
                <span className="pt-0.5">{item}</span>
              </li>
            ))}
          </ol>
        </Section>
      ))}

      {warnings.length > 0 && (
        <Section icon={AlertTriangle} title="Dikkat" tone="warning">
          <ul className="space-y-1.5">
            {warnings.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-amber-900">
                <span className="text-amber-600 mt-0.5">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {related.length > 0 && (
        <Section title="İlgili sayfalar">
          <div className="flex flex-wrap gap-2">
            {related.map((rel) => (
              <button
                key={rel.page_id}
                type="button"
                onClick={() => onNavigate && onNavigate(rel.page_id)}
                disabled={!onNavigate}
                className="inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded-md border border-gray-200 text-green-700 hover:bg-green-50 disabled:text-gray-500 disabled:hover:bg-transparent disabled:cursor-default"
              >
                {rel.label}
                {onNavigate && <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
};

export default HelpContentView;
